import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonCard } from '../../components/ui/Skeleton';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { fmtEur, fmtDate } from '../../lib/utils';
import {
  riconciliazioneQuery, pagamentiGrandiQuery, rateMancantiQuery, plurale,
  type PropostaRic, type Certezza, type RiconciliazioneData,
} from './adminApi';
import { useAdminActions } from './useAdminActions';

/**
 * Da riconciliare — dove la banca e i documenti si guardano in faccia.
 *
 * In cima la fotografia: quanto risulta aperto, quanto si chiude da solo perché
 * il bonifico c'è ed è inequivocabile, quanto invece va guardato da una persona.
 *
 * Le proposte sono divise per quanto sono solide, e ogni riga porta la prova
 * accanto (movimento, data, nome sul bonifico) più la frase che spiega perché è
 * finita in quel gruppo. Si conferma o si scarta con un gesto solo.
 *
 * Tre livelli, e la differenza è tutta qui:
 *   CERTO     importo + nome + data, un solo movimento possibile → lo chiude il computer
 *   PROBABILE due indizi su tre → lo propone, decide Lorenzo
 *   DUBBIO    solo l'importo, o più movimenti identici → non si chiude mai da solo
 *
 * Il DUBBIO non è pigrizia: ci sono quattro fatture di Edoardo Del Sarto da
 * 1.374,80 e in banca due addebiti da 1.374,80. Chiudere sull'importo avrebbe
 * scelto le due sbagliate e cancellato un debito vero.
 */
export function RiconciliazioneTab({ canWrite }: { canWrite: boolean }) {
  const q = useQuery(riconciliazioneQuery);
  const { applicaRiconciliazione } = useAdminActions();
  const d = q.data;

  if (q.isLoading && !d) {
    return <div className="adm-bento"><SkeletonCard titleW={200} blocks={3} /><SkeletonCard titleW={160} blocks={3} /></div>;
  }
  if (!d) return <EmptyState brand title="Riconciliazione non disponibile" description="L'API non risponde: riprova tra un momento." />;

  const certi = d.aperte.filter((p) => p.certezza === 'CERTO');
  const probabili = d.aperte.filter((p) => p.certezza === 'PROBABILE');
  const dubbi = d.aperte.filter((p) => p.certezza === 'DUBBIO');
  const valoreCerti = certi.reduce((s, p) => s + p.importo, 0);
  const daGuardare = probabili.reduce((s, p) => s + p.importo, 0) + dubbi.reduce((s, p) => s + p.importo, 0);
  const agganciApplicabili = d.agganci.filter((p) => p.certezza === 'CERTO' || (p.certezza === 'PROBABILE' && p.alternativi === 0 && p.indizi.nome === 'FORTE'));

  return (
    <>
      <Fotografia d={d} valoreCerti={valoreCerti} certiCount={certi.length} daGuardare={daGuardare} />

      <DesktopOnly
        cosa="La riconciliazione tra banca e documenti"
        perche="richiede di confrontare indizi, movimenti e rate riga per riga. Dal telefono resta la fotografia completa degli importi."
      >
      <>
      {canWrite && (certi.length > 0 || agganciApplicabili.length > 0) && (
        <div className="adm-note">
          <Icon name="zap" size={12} className="mute" />
          <span className="flex-1">
            {certi.length > 0
              /* La frase parte dal conteggio invece di "Ci sono N rate": con
                 una sola rata diceva "Ci sono 1 rate", e per accordare il
                 verbo servivano tre ternari in una riga. Così il numero non
                 deve accordarsi con niente. */
              ? <><strong>{plurale(certi.length, 'rata', 'rate')}</strong> per <strong>€ {fmtEur(valoreCerti)}</strong> con il bonifico giusto, il nome giusto e la data giusta: da chiudere adesso.</>
              : <>Nessuna rata da chiudere, ma <strong>{plurale(agganciApplicabili.length, 'movimento', 'movimenti')}</strong> già {agganciApplicabili.length === 1 ? 'spiegato' : 'spiegati'} da una fattura, da agganciare.</>}
            {agganciApplicabili.length > 0 && certi.length > 0 && <> Insieme si agganciano anche <strong>{plurale(agganciApplicabili.length, 'movimento', 'movimenti')}</strong> di rate già pagate.</>}
            {' '}Si può annullare tutto.
          </span>
          <button
            className="btn btn-sm is-active"
            disabled={applicaRiconciliazione.isPending}
            onClick={() => applicaRiconciliazione.mutate()}
          >
            <Icon name="check" size={10} /> {applicaRiconciliazione.isPending ? 'Chiudo…' : 'Chiudi i certi'}
          </button>
        </div>
      )}

      <Gruppo
        titolo="Certe"
        icona="check"
        descrizione="Importo, nome e data combaciano e non c'è un altro movimento possibile."
        righe={certi}
        canWrite={canWrite}
        vuoto="Niente da chiudere in automatico."
      />
      <Gruppo
        titolo="Probabili"
        icona="help"
        descrizione="Due indizi su tre. Il computer non le chiude: guardale e decidi."
        righe={probabili}
        canWrite={canWrite}
        vuoto="Nessuna proposta in sospeso."
      />
      <Gruppo
        titolo="Dubbie"
        icona="alert"
        descrizione="Solo l'importo, oppure più movimenti identici fra cui scegliere. Qui l'errore costa: un bonifico sotto la fattura sbagliata cancella un credito vero."
        righe={dubbi}
        canWrite={canWrite}
        vuoto="Nessun caso ambiguo."
      />

      <Agganci righe={d.agganci} canWrite={canWrite} />
      <RateAuto canWrite={canWrite} />
      <PagamentiGrandi />
      <FuoriDaiConti d={d} />
      <Scartate righe={d.scartate} canWrite={canWrite} />
      <Lotti lotti={d.lotti} canWrite={canWrite} />
      </>
      </DesktopOnly>
    </>
  );
}

// ── La fotografia: i quattro numeri in cima ────────────────────────────────

function Fotografia({ d, valoreCerti, certiCount, daGuardare }: { d: RiconciliazioneData; valoreCerti: number; certiCount: number; daGuardare: number }) {
  const f = d.fotografia;
  const dopo = Math.round((f.totaleAperto - valoreCerti) * 100) / 100;
  return (
    /* `.stat-band` porta già i suoi 10px: era l'unica fascia di numeri
       dell'applicazione a staccarsi di 16, e qui se ne vedono due una sotto
       l'altra (quella della pagina e questa), quindi la differenza si notava. */
    <div className="stat-band">
      <Cella
        label="Risulta aperto"
        value={f.totaleAperto}
        sub={`${plurale(f.incassi.rate, 'rata', 'rate')} da incassare · ${f.uscite.rate} da pagare`}
      />
      <Cella
        label="Si chiude subito"
        value={valoreCerti}
        sub={certiCount ? `${plurale(certiCount, 'rata', 'rate')} con il bonifico dietro` : 'niente di inequivocabile'}
      />
      <Cella
        label="Da guardare"
        value={daGuardare}
        sub={`${plurale(d.aperte.length - certiCount, 'proposta', 'proposte')} da confermare o scartare`}
      />
      <Cella
        label="Resterebbe aperto"
        value={dopo}
        sub={valoreCerti > 0 ? `− € ${fmtEur(valoreCerti)} rispetto a oggi` : 'nessuna differenza'}
      />
    </div>
  );
}

function Cella({ label, value, sub }: { label: string; value: number; sub: string }) {
  return (
    <div>
      <div className="st-label">{label}</div>
      <div className="st-value"><span className="u">€</span>{fmtEur(value)}</div>
      <div className="st-delta">{sub}</div>
    </div>
  );
}

// ── Un gruppo di proposte ──────────────────────────────────────────────────

const TONO: Record<Certezza, 'ok' | 'warn' | 'info'> = { CERTO: 'ok', PROBABILE: 'warn', DUBBIO: 'info' };

function Gruppo({ titolo, icona, descrizione, righe, canWrite, vuoto }: {
  titolo: string;
  icona: 'check' | 'help' | 'alert';
  descrizione: string;
  righe: PropostaRic[];
  canWrite: boolean;
  vuoto: string;
}) {
  const totale = righe.reduce((s, p) => s + p.importo, 0);
  return (
    <div className="card mb-4">
      <div className="card-hd">
        <div className="flex items-center gap-3">
          <div className="icon-tile sm"><Icon name={icona} size={14} /></div>
          <div>
            <div className="t-h3">{titolo}{righe.length > 0 && <span className="mute"> · {righe.length}</span>}</div>
            <div className="mute t-small">{descrizione}</div>
          </div>
        </div>
        {righe.length > 0 && <div className="mono font-semibold" style={{ whiteSpace: 'nowrap' }}>€ {fmtEur(totale)}</div>}
      </div>
      <div className="card-bd card-bd--tight">
        {righe.length === 0
          ? <EmptyState icon="check" title={vuoto} />
          : <Tabella righe={righe} canWrite={canWrite} />}
      </div>
    </div>
  );
}

function Tabella({ righe, canWrite, soloAggancio }: { righe: PropostaRic[]; canWrite: boolean; soloAggancio?: boolean }) {
  const { confermaProposta, scartaProposta } = useAdminActions();
  // `.adm-clip` vale `max-width: 0`: serve sul computer per troncare le etichette
  // Qonto chilometriche, ma sul telefono la riga diventa una scheda a blocchi e
  // quella larghezza zero cancellerebbe il testo. Lo disinnesca admin.css con
  // `.tbl--card td.adm-clip`, non più un `useIsMobile()`: la stessa decisione
  // era presa in JS qui e in CSS altrove, e in JS costa anche un render.
  return (
    /* `tbl--static`: la riga non apre un dettaglio, il gesto è Conferma/Scarta
       dentro la scheda, quindi niente chevron "si apre". */
    <table className="tbl tbl--ed tbl--card tbl--static">
      <thead>
        <tr>
          {/* Le tre colonne elastiche (.adm-clip) si spartiscono lo spazio che
              resta; le percentuali tengono il documento largo quanto serve a
              leggere il nome del cliente, che e' la cosa che si cerca per prima. */}
          <th style={{ width: '28%' }}>Documento</th>
          <th style={{ width: 104 }}>Certezza</th>
          <th className="num" style={{ width: 112 }}>Importo</th>
          <th style={{ width: '24%' }}>Il movimento in banca</th>
          <th style={{ width: '20%' }}>Perché</th>
          {canWrite && <th style={{ width: 132 }} />}
        </tr>
      </thead>
      <tbody>
        {righe.map((p) => (
          <tr key={p.key} style={{ cursor: 'default' }}>
            <td data-m="hd" className="adm-clip">
              <div className="font-semibold truncate">{p.controparte || '(senza nome)'}</div>
              <div className="text-[12px] mute truncate">
                {p.lato === 'INCASSO' ? 'fattura ' : 'fornitore '}{p.docNumero ?? 's/n'}
                {p.dueDate && ` · scadenza ${fmtDate(p.dueDate)}`}
              </div>
            </td>
            <td data-m="key" data-l="Certezza">
              <Chip tone={TONO[p.certezza]}>{p.certezza.toLowerCase()}</Chip>
              <Indizi p={p} />
            </td>
            <td data-m="key" data-l="Importo" className="num mono font-semibold" style={{ whiteSpace: 'nowrap' }}>€ {fmtEur(p.importo, true)}</td>
            <td data-m="key" data-l="Movimento" className="adm-clip">
              <div className="truncate text-[13px]">{p.movimento.counterparty || p.movimento.label}</div>
              <div className="text-[12px] mute">
                {/* Data e cifra restano insieme: in scheda la colonna è larga
                    155px e la riga andava a capo fra "€" e "1220,00". */}
                <span className="whitespace-nowrap">{fmtDate(p.movimento.settledOn)} · € {fmtEur(p.movimento.amount, true)}</span>
                {p.movimento.accountName && ` · ${p.movimento.accountName}`}
              </div>
            </td>
            {/* Il perché va in un <div>: .adm-clip tronca solo figli di blocco,
                con uno <span> il testo lungo esce dalla tabella e finisce sotto
                i pulsanti. */}
            <td data-m="row" data-l="Perché" className="adm-clip">
              <div className="text-[12px] mute" title={p.motivo}>{p.motivo}</div>
            </td>
            {/* Sul telefono i due pulsanti prendono una riga tutta loro in fondo
                alla scheda ("row"): ancorati in alto a destra ("act")
                finirebbero sopra il nome del cliente, che è la prima cosa che
                si legge. `data-m` sul computer non fa niente, quindi il valore
                è uno solo e non serve chiedere a JS su che schermo siamo.
                L'allineamento a sinistra in scheda lo fa admin.css. */}
            {canWrite && (
              <td data-m="row" style={{ whiteSpace: 'nowrap' }}>
                <div className="flex items-center gap-1 justify-end">
                  <button
                    className="btn btn-sm"
                    title={soloAggancio ? 'Collega questo movimento alla rata' : 'Registra il pagamento con questo movimento'}
                    disabled={confermaProposta.isPending}
                    onClick={() => confermaProposta.mutate({
                      lato: p.lato,
                      rataId: p.rataId,
                      txId: p.movimento.id,
                      paidDate: p.movimento.settledOn ?? p.dueDate ?? '',
                      label: `${p.docNumero ?? 's/n'} · ${p.controparte}`,
                      importo: p.importo,
                    })}
                  >
                    <Icon name="check" size={10} /> Conferma
                  </button>
                  {/* `btn-icon`: è un bottone di sola icona come la matita e il
                      cestino del Fisco, e senza quella classe restava largo
                      24px — sul telefono mobile.css gli dà il riquadro di
                      tocco da 44 solo se ce l'ha. */}
                  <button
                    className="btn btn-sm btn-ghost btn-icon"
                    aria-label="Scarta la proposta"
                    title="Non è questo il movimento: non riproporlo"
                    disabled={scartaProposta.isPending}
                    onClick={() => scartaProposta.mutate({ rataId: p.rataId, txId: p.movimento.id, label: `${p.docNumero ?? 's/n'} · ${p.controparte}` })}
                  >
                    <Icon name="x" size={10} />
                  </button>
                </div>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** I tre indizi in chiaro: si capisce al volo perché una riga è certa e un'altra no. */
function Indizi({ p }: { p: PropostaRic }) {
  const nome = p.indizi.nome === 'FORTE' ? 'nome' : p.indizi.nome === 'DEBOLE' ? 'nome~' : null;
  const voci = ['importo', nome, p.indizi.data ? 'data' : null].filter(Boolean) as string[];
  return <div className="text-[11px] mute" style={{ marginTop: 2 }}>{voci.join(' · ')}</div>;
}

// ── Agganci: rate già pagate che ritrovano il loro bonifico ────────────────

function Agganci({ righe, canWrite }: { righe: PropostaRic[]; canWrite: boolean }) {
  const [aperto, setAperto] = useState(false);
  if (!righe.length) return null;
  const totale = righe.reduce((s, p) => s + p.importo, 0);
  return (
    <div className="card mb-4">
      <div className="card-hd">
        <div className="flex items-center gap-3">
          <div className="icon-tile sm"><Icon name="link" size={14} /></div>
          <div>
            <div className="t-h3">Movimenti già spiegati <span className="mute">· {righe.length}</span></div>
            <div className="mute t-small">
              Rate che risultano già pagate e hanno ritrovato il loro bonifico. Agganciarle non cambia nessun numero:
              serve a togliere dal mucchio i movimenti che un documento spiega già, così smettono di farsi proporre per altre fatture.
            </div>
          </div>
        </div>
        <button className="btn btn-sm btn-ghost" onClick={() => setAperto(!aperto)}>
          <Icon name={aperto ? 'chevronU' : 'chevronD'} size={10} /> € {fmtEur(totale)}
        </button>
      </div>
      {aperto && <div className="card-bd card-bd--tight"><Tabella righe={righe} canWrite={canWrite} soloAggancio /></div>}
    </div>
  );
}

// ── Le rate delle auto finanziate ──────────────────────────────────────────

function RateAuto({ canWrite }: { canWrite: boolean }) {
  const q = useQuery(rateMancantiQuery);
  const { creaRateMancanti } = useAdminActions();
  const items = (q.data?.items ?? []).filter((r) => r.addebiti > 0);
  if (!items.length) return null;
  return (
    <div className="card mb-4">
      <div className="card-hd">
        <div className="flex items-center gap-3">
          <div className="icon-tile sm"><Icon name="repeat" size={14} /></div>
          <div>
            <div className="t-h3">Rate che nessuno ha registrato</div>
            <div className="mute t-small">
              Le auto sono marcate finanziate e tolte dai debiti, giusto. Ma le rate che le stanno pagando non esistono
              da nessuna parte: in banca gli addebiti ci sono, nel gestionale no.
            </div>
          </div>
        </div>
      </div>
      <div className="card-bd card-bd--tight">
        {items.map((r) => (
          <div key={r.recurringId} className="adm-line" style={{ alignItems: 'flex-start' }}>
            <div className="min-w-0 flex-1">
              <div className="truncate"><strong>{r.nome}</strong>{r.documenti.length > 0 && <span className="mute"> · sta pagando {r.documenti.map((x) => `${x.fornitore} (€ ${fmtEur(x.totale)})`).join(', ')}</span>}</div>
              <div className="text-[12px] mute">
                Nel gestionale risulta dal {r.dalGestionale ? fmtDate(r.dalGestionale) : '—'}, in banca si paga dal{' '}
                <strong>{r.dallaBanca ? fmtDate(r.dallaBanca) : '—'}</strong>: {plurale(r.addebiti, 'addebito mai registrato', 'addebiti mai registrati')}.
              </div>
              <div className="text-[12px] mute truncate" style={{ marginTop: 2 }}>
                {r.movimenti.slice(0, 8).map((m) => `${fmtDate(m.settledOn)} € ${fmtEur(m.amount, true)}`).join(' · ')}
                {r.movimenti.length > 8 && ` · e altri ${r.movimenti.length - 8}`}
              </div>
            </div>
            <div className="mono font-semibold text-right" style={{ width: 110, whiteSpace: 'nowrap' }}>€ {fmtEur(r.totaleAddebiti)}</div>
            {canWrite && (
              <button
                className="btn btn-sm"
                disabled={creaRateMancanti.isPending}
                title="Registra questi addebiti come uscite già pagate, con la data e l'importo veri"
                onClick={() => creaRateMancanti.mutate({ recurringId: r.recurringId, nome: r.nome, addebiti: r.addebiti, totale: r.totaleAddebiti })}
              >
                <Icon name="plus" size={10} /> Registra
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── I pagamenti grandi ─────────────────────────────────────────────────────

function PagamentiGrandi() {
  const [aperto, setAperto] = useState(false);
  const q = useQuery({ ...pagamentiGrandiQuery, enabled: aperto });
  const items = q.data?.items ?? [];
  // Prima quelli che non tornano: una rata dice "bonifico" e in banca non c'è.
  // Non basta "senza movimento": compensazioni, scissione, contanti e carta non
  // passano dalla banca, e segnalarle sarebbe un falso allarme su mezza lista.
  const problemi = items.filter((r) => r.rate.buchi > 0 || (r.aperto > 0 && r.stato !== 'ANNULLATA' && r.stato !== 'FINANZIATA'));
  return (
    <div className="card mb-4">
      <div className="card-hd">
        <div className="flex items-center gap-3">
          <div className="icon-tile sm"><Icon name="coins" size={14} /></div>
          <div>
            <div className="t-h3">Pagamenti sopra i 5.000</div>
            <div className="mute t-small">
              Quante rate erano previste, quante risultano registrate e quante hanno davvero un movimento dietro.
              Una rata segnata incassata senza bonifico è una parola di FattureInCloud, non un fatto della banca.
            </div>
          </div>
        </div>
        <button className="btn btn-sm btn-ghost" onClick={() => setAperto(!aperto)}>
          <Icon name={aperto ? 'chevronU' : 'chevronD'} size={10} /> {aperto ? 'Nascondi' : 'Mostra'}
        </button>
      </div>
      {aperto && (
        <div className="card-bd card-bd--tight">
          {q.isLoading ? <div className="mute t-small">Carico…</div> : (
            <>
              <div className="mute t-small mb-2">
                {plurale(items.length, 'documento', 'documenti')} sopra i 5.000. {problemi.length
                  ? <>Ne {problemi.length === 1 ? 'resta' : 'restano'} <strong>{problemi.length}</strong> che non {problemi.length === 1 ? 'torna' : 'tornano'}: o {problemi.length === 1 ? 'è ancora aperto' : 'sono ancora aperti'}, o una rata dice “bonifico” e in banca non c’è.</>
                  : 'Tornano tutti.'}
              </div>
              <table className="tbl tbl--ed tbl--card tbl--static">
                <thead>
                  <tr>
                    <th>Documento</th>
                    <th className="num" style={{ width: 120 }}>Totale</th>
                    <th style={{ width: 150 }}>Rate</th>
                    <th>Cosa manca</th>
                  </tr>
                </thead>
                <tbody>
                  {[...problemi, ...items.filter((r) => !problemi.includes(r))].map((r) => (
                    <tr key={`${r.lato}-${r.docId}`} style={{ cursor: 'default' }}>
                      <td data-m="hd" className="adm-clip">
                        <div className="font-semibold truncate">{r.controparte}</div>
                        <div className="text-[12px] mute truncate">
                          {r.lato === 'INCASSO' ? 'emessa' : 'ricevuta'} {r.numero ?? 's/n'} · {fmtDate(r.dataDocumento)} · {r.stato.toLowerCase()}
                        </div>
                      </td>
                      <td data-m="key" data-l="Totale" className="num mono font-semibold" style={{ whiteSpace: 'nowrap' }}>€ {fmtEur(r.totale, true)}</td>
                      <td data-m="key" data-l="Rate">
                        <span className="mono text-[13px]">{r.rate.chiuse}/{r.rate.previste}</span>
                        <span className="mute text-[12px]"> chiuse · {r.rate.conMovimento} con movimento</span>
                        {r.rate.buchi > 0 && <div className="text-[12px] text-alert">{r.rate.buchi} senza il bonifico</div>}
                      </td>
                      <td data-m="row" data-l="Cosa manca" className="adm-clip">
                        <div className="text-[12px] mute" title={r.diagnosi}>{r.diagnosi}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Il rumore e i movimenti orfani ─────────────────────────────────────────

function FuoriDaiConti({ d }: { d: RiconciliazioneData }) {
  // In cima va solo il denaro contato DUE volte (prestito e giroconti): mettere
  // insieme anche gli accrediti Stripe direbbe che 47.000 euro incassati davvero
  // sono rumore, e non è vero.
  const doppio = d.rumore.filter((r) => r.gonfiaIConti).reduce((s, r) => s + r.totale, 0);
  return (
    <div className="card mb-4">
      <div className="card-hd">
        <div className="flex items-center gap-3">
          <div className="icon-tile sm"><Icon name="unlink" size={14} /></div>
          <div>
            <div className="t-h3">Movimenti che non si abbinano</div>
            <div className="mute t-small">Perché nessun documento li spiegherà mai, uno per uno.</div>
          </div>
        </div>
        <div className="text-right" style={{ whiteSpace: 'nowrap' }}>
          <div className="mono font-semibold">€ {fmtEur(doppio)}</div>
          <div className="text-[11px] mute">contati due volte</div>
        </div>
      </div>
      <div className="card-bd card-bd--tight">
        {d.rumore.map((r) => (
          <div key={r.motivo} className="adm-line">
            <div className="min-w-0 flex-1">
              <div className="text-[13px] truncate" title={r.etichetta}>{r.etichetta}</div>
              <div className="text-[12px] mute">{plurale(r.movimenti, 'movimento', 'movimenti')}{r.gonfiaIConti ? ' · lo stesso denaro entra ed esce, va tolto da entrambi i lati' : ' · resta nei conti, ma senza documento accanto'}</div>
            </div>
            <div className="mono font-semibold text-right" style={{ width: 110, whiteSpace: 'nowrap' }}>€ {fmtEur(r.totale)}</div>
          </div>
        ))}
        {d.fotografia.suCestinate.rate > 0 && (
          <div className="adm-line">
            <div className="min-w-0 flex-1">
              <div className="text-[13px] truncate">Rate appese a fatture cestinate: nessuno ce le deve</div>
              <div className="text-[12px] mute">{plurale(d.fotografia.suCestinate.rate, 'rata', 'rate')}, già fuori dai totali qui sopra</div>
            </div>
            <div className="mono font-semibold text-right" style={{ width: 110, whiteSpace: 'nowrap' }}>€ {fmtEur(d.fotografia.suCestinate.totale)}</div>
          </div>
        )}
        {/* 10 non sta sulla griglia 4/8: 12, che è `--s-3`. */}
        <div className="adm-note mt-3">
          <Icon name="help" size={12} className="mute" />
          <span>
            {d.senzaDocumento.movimenti === 1 ? 'Resta' : 'Restano'} <strong>{plurale(d.senzaDocumento.movimenti, 'movimento', 'movimenti')}</strong> per <strong>€ {fmtEur(d.senzaDocumento.totale)}</strong> che
            nessun documento spiega.{' '}
            {d.senzaDocumento.perAnno.map((a) => `${a.anno}: € ${fmtEur(a.uscite)} usciti, € ${fmtEur(a.entrate)} entrati`).join(' · ')}.
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Le proposte scartate ───────────────────────────────────────────────────

function Scartate({ righe, canWrite }: { righe: PropostaRic[]; canWrite: boolean }) {
  const [aperto, setAperto] = useState(false);
  const { ripristinaScarto } = useAdminActions();
  if (!righe.length) return null;
  return (
    <div className="card mb-4">
      <div className="card-hd">
        <div className="flex items-center gap-3">
          <div className="icon-tile sm"><Icon name="eyeOff" size={14} /></div>
          <div>
            <div className="t-h3">Scartate <span className="mute">· {righe.length}</span></div>
            <div className="mute t-small">Proposte che hai già guardato e detto di no. Se domani arriva un bonifico diverso, la rata torna in lista da sola.</div>
          </div>
        </div>
        <button className="btn btn-sm btn-ghost" onClick={() => setAperto(!aperto)}>
          <Icon name={aperto ? 'chevronU' : 'chevronD'} size={10} /> {aperto ? 'Nascondi' : 'Mostra'}
        </button>
      </div>
      {aperto && (
        <div className="card-bd card-bd--tight">
          {righe.map((p) => (
            <div key={p.key} className="adm-line">
              <div className="min-w-0 flex-1">
                <div className="truncate"><strong>{p.controparte}</strong><span className="mute"> · {p.docNumero ?? 's/n'}</span></div>
                <div className="text-[12px] mute truncate">movimento del {fmtDate(p.movimento.settledOn)} · {p.movimento.counterparty || p.movimento.label}</div>
              </div>
              <div className="mono font-semibold text-right" style={{ width: 100, whiteSpace: 'nowrap' }}>€ {fmtEur(p.importo, true)}</div>
              {canWrite && (
                <button className="btn btn-sm btn-ghost" onClick={() => ripristinaScarto.mutate({ rataId: p.rataId, txId: p.movimento.id })}>
                  <Icon name="undo" size={10} /> Rimetti
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── I lotti già eseguiti: da qui si torna indietro ─────────────────────────

function Lotti({ lotti, canWrite }: { lotti: RiconciliazioneData['lotti']; canWrite: boolean }) {
  const { annullaLotto } = useAdminActions();
  if (!lotti.length) return null;
  return (
    <div className="card mb-4">
      <div className="card-hd">
        <div className="flex items-center gap-3">
          <div className="icon-tile sm"><Icon name="undo" size={14} /></div>
          <div>
            <div className="t-h3">Riconciliazioni fatte</div>
            <div className="mute t-small">Ogni chiusura automatica è un lotto, e ogni lotto si annulla: le rate tornano come erano, con la loro data e il loro conto.</div>
          </div>
        </div>
      </div>
      <div className="card-bd card-bd--tight">
        {lotti.map((l) => (
          <div key={l.lotto} className="adm-line">
            <div className="min-w-0 flex-1">
              <div className="text-[13px]">{fmtDate(l.quando)} · {plurale(l.chiuse, 'rata chiusa', 'rate chiuse')}, {plurale(l.agganciate, 'movimento agganciato', 'movimenti agganciati')}</div>
              <div className="text-[12px] mute">{l.annullato ? 'annullato' : `€ ${fmtEur(l.valore)} di rate saldate`}</div>
            </div>
            {canWrite && !l.annullato && (
              <button className="btn btn-sm btn-ghost" disabled={annullaLotto.isPending} onClick={() => annullaLotto.mutate({ lotto: l.lotto, chiuse: l.chiuse })}>
                <Icon name="undo" size={10} /> Annulla
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
